import { html } from '/vendor/htm-preact.module.js';
import { Icon } from '/icons.js';

/**
 * Blackball, for the two people at the table.
 *
 * The rules as data rather than a link, because the one place you need them
 * is a cellar with no signal, and a link is exactly what does not work there.
 * Every line carries its rule number so a disagreement can be settled against
 * the actual published text rather than against this summary.
 *
 * TWO RULESETS, and this prose describes the DEFAULT one. `rules.js` knows
 * `BDIPL Blackball` and `EPA Blackball`; they differ on exactly one question,
 * and the line that differs says so and gives both answers. A house rule
 * presented as the published rule is how somebody loses an argument they
 * should have won, so it is labelled rather than smoothed over.
 *
 * Ordered by what somebody actually asks mid-frame — "is that a foul", "what
 * do I get for it" — not by the numbering, which runs equipment first.
 *
 * `rules.js` is the state machine; this is the prose. They are kept apart on
 * purpose: one is executable and tested, the other is read by a human, and
 * pretending a paragraph is a specification is how the two drift.
 */
export const RULEBOOK = [
  {
    title: 'The break',
    cite: '4b, 4d',
    lines: [
      'Played from baulk at the triangle.',
      'It is a legal break if a ball is potted, or at least two object balls '
      + 'fully cross the middle-pocket line.',
      'An illegal break gives the opponent a free shot plus one visit. They '
      + 'choose: play from where the cue ball lies, play from baulk, or call a '
      + 're-rack — and if they call it, they break.',
      'The black potted on the break is a re-rack with no penalty, and the '
      + 'same player breaks again.',
    ],
  },
  {
    title: 'Whose colour is whose',
    cite: '4e, 4f',
    lines: [
      'The table stays OPEN and groups are not decided: on the break, on a '
      + 'foul shot, on a free shot after a foul, and on a combination that '
      + 'pots both colours.',
      'Otherwise the first legal pot decides both groups at once.',
      'Balls potted on a foul are ignored in deciding groups — but they have '
      + 'still left the table.',
    ],
  },
  {
    title: 'Staying at the table',
    cite: '4g',
    lines: [
      'Pot a ball with a legal shot and you play again.',
      'That continues until you fail to pot one of your own, or you foul.',
    ],
  },
  {
    title: 'What a foul is',
    cite: '5',
    lines: [
      'Potting the cue ball in-off, including on the break. (5a)',
      'Potting an opponent’s ball without one of your own — except when '
      + 'the table is open. (5c)',
      'Failing to play a legal shot: the cue ball must strike an ‘on’ '
      + 'ball first, and then either a ball is potted or some ball reaches a '
      + 'cushion. (5d)',
      'Escaping a snooker needs only to strike an ‘on’ ball — no pot '
      + 'and no cushion required. (5g)',
      'Jump shots, push strokes, double hits, playing out of turn, playing '
      + 'before the balls have stopped, or striking the cue ball with anything '
      + 'but the tip. (5e, 5f, 5h, 5i, 5k, 5m)',
    ],
  },
  {
    title: 'What a foul costs',
    cite: '6a, 6b, 6c',
    lines: [
      'The offender loses their next visit. The opponent gets a FREE SHOT '
      + 'plus one visit.',
      'On the free shot — the first shot only — the cue ball may be played '
      + 'onto any ball, and any ball may be potted, without penalty.',
      'That includes potting the opponent’s balls, and playing the black '
      + 'onto them.',
      'The cue ball may be placed in baulk or played from where it lies. '
      + 'Moving it within baulk is not a shot.',
      'HOUSE RULE — Potting on the free shot SPENDS the visit you were owed: '
      + 'you carry on as normal, and the spare visit is what you get instead '
      + 'when the free shot puts nothing down. Under EPA Blackball you keep '
      + 'both — 6(C) says a pot on the free shot continues the first visit, '
      + 'and only failing to pot moves you on to the second.',
    ],
  },
  {
    title: 'Winning the frame',
    cite: '4s, 7b',
    lines: [
      'Pot all seven of your own group and then the black, on legal shots.',
      'Your last colour and the black in the same shot is a win — what counts '
      + 'is that none of your group remains after the shot.',
    ],
  },
  {
    title: 'Losing the frame',
    cite: '7',
    lines: [
      'Committing a foul in the shot that pots the black. (7a)',
      'Potting the black while any of your own group is still on the table. '
      + '(7b)',
      'A deliberate foul: clearly and intentionally playing a ball not '
      + '‘on’, or not making a bona fide attempt at a legal shot. (7c)',
      'Touching or picking up a ball without permission. (7d)',
      'Catching the cue ball to stop it going in-off. (7e)',
      'Disrupting the opponent’s play by word or action. (7f)',
    ],
  },
  {
    title: 'Stalemate',
    cite: '4r',
    lines: [
      'If no legal shot is playable, the frame is re-started.',
      'By accident, the original breaker breaks again. By design or negative '
      + 'play, the players lag for it.',
    ],
  },
  {
    title: 'What this app will not judge',
    cite: null,
    lines: [
      'It cannot see a cushion, a push stroke or a snooker, so it never calls '
      + 'a foul on its own — you tap FOUL and it applies the penalty.',
      'The referee is the two of you. This only keeps the score.',
    ],
  },
];

export function Rulebook({ onDone }) {
  return html`
    <div class="matchbar">
      <button data-testid="rules-back" onClick=${onDone} aria-label="Back">
        <${Icon} name="chevron-left" size=${20} />
      </button>
      <span>BLACKBALL RULES</span>
      <span style="width:20px"></span>
    </div>

    <div class="screen rules">
      <p class="sub">
        The WPA blackball rules, in the order you tend to need them. Numbers
        are the rule they come from.
      </p>

      ${RULEBOOK.map((section) => html`
        <div key=${section.title} data-testid="rules-section">
          <div class="rowbetween" style="margin-bottom:.5rem">
            <label style="margin:0">${section.title}</label>
            ${section.cite && html`
              <span class="sub mono cite">${section.cite}</span>`}
          </div>
          ${section.lines.map((line, i) => html`
            <div class="card" key=${i} data-testid="rules-line">
              ${line}
            </div>`)}
        </div>`)}

      <button class="secondary" data-testid="rules-done" onClick=${onDone}>
        Back
      </button>
    </div>`;
}
